import { ArrowClockwiseIcon } from '@phosphor-icons/react';
import {
  Button,
  buttonStyles,
  Checkbox,
  PageToolbar,
  SearchField,
  Switch,
  Tooltip,
} from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import { StorageProviderHelpText } from '@utility/constants/strings';
import { PermissionGate } from '../PermissionGate';

export type InventoryToolbarProps = {
  showEmpty: boolean;
  onShowEmptyChange: (showEmpty: boolean) => void;
  imperial: boolean;
  onImperialChange: (imperial: boolean) => void;
  hasItems: boolean;
  /** Items across every page; hidden while they load. */
  total?: number;
  search: string;
  onSearchChange: (search: string) => void;
  /** The provider's stock can be pulled again on demand. */
  canRefresh: boolean;
  isRefreshing: boolean;
  onRefresh: () => void;
  /** The organisation's inventory sheet; none hides Bulk Update. */
  bulkUpdate?: { href: string; disabled: boolean; hidden: boolean };
};

/** Filters, units and search above the inventory table, with its actions. */
export function InventoryToolbar({
  showEmpty,
  onShowEmptyChange,
  imperial,
  onImperialChange,
  hasItems,
  total,
  search,
  onSearchChange,
  canRefresh,
  isRefreshing,
  onRefresh,
  bulkUpdate,
}: InventoryToolbarProps) {
  return (
    <PageToolbar
      className="sk:mx-0"
      leading={
        <div className="sk:flex sk:items-center sk:gap-4">
          <Checkbox isSelected={showEmpty} onChange={onShowEmptyChange}>
            Show items with no inventory
          </Checkbox>
          <Switch
            isSelected={imperial}
            onChange={onImperialChange}
            isDisabled={!hasItems}
          >
            Imperial
          </Switch>
        </div>
      }
    >
      {typeof total === 'number' ? (
        <span className="sk:text-dense sk:tabular-nums sk:text-fg-secondary">
          {CommonUtility.numberWithCommas(total)} SKUs
        </span>
      ) : null}
      <SearchField
        aria-label="Search items"
        placeholder="SKU, UPC, Title, ASIN..."
        value={search}
        onChange={onSearchChange}
        className="sk:w-64"
      />
      {canRefresh ? (
        <Button onPress={onRefresh} isPending={isRefreshing}>
          <ArrowClockwiseIcon size={14} aria-hidden />
          Refresh
        </Button>
      ) : null}
      {bulkUpdate ? (
        bulkUpdate.disabled || bulkUpdate.hidden ? (
          <PermissionGate
            disabled={bulkUpdate.disabled}
            hidden={bulkUpdate.hidden}
            placement="bottom end"
          >
            <Button isDisabled>Bulk Update</Button>
          </PermissionGate>
        ) : (
          <Tooltip
            content={StorageProviderHelpText.importTooltip}
            placement="bottom end"
          >
            <a
              href={bulkUpdate.href}
              target="_blank"
              rel="noreferrer"
              className={buttonStyles({ className: 'sk:no-underline' })}
            >
              Bulk Update
            </a>
          </Tooltip>
        )
      ) : null}
    </PageToolbar>
  );
}
